import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('session_token');
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setUser(response.data);
      return true;
    } catch (error) {
      setUser(null);
      localStorage.removeItem('session_token');
      return false;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    // Legacy: skip if old Emergent SSO callback is in progress
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('session_token');
      localStorage.removeItem('original_admin_token');
      setUser(null);
      window.location.href = '/';
    }
  };

  const impersonate = async (targetUserId) => {
    try {
      const currentToken = localStorage.getItem('session_token');
      const response = await axios.post(`${API_URL}/auth/impersonate/${targetUserId}`);
      localStorage.setItem('original_admin_token', currentToken);
      localStorage.setItem('session_token', response.data.session_token);
      // Force full page reload to refresh all data with new user context
      // Adding timestamp ensures the browser treats it as a new navigation
      window.location.href = '/dashboard?t=' + Date.now();
    } catch (error) {
      console.error('Impersonate error:', error);
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/stop-impersonation`);
      localStorage.removeItem('original_admin_token');
      localStorage.setItem('session_token', response.data.session_token);
      window.location.href = '/dashboard?t=' + Date.now();
    } catch (error) {
      console.error('Stop impersonation error:', error);
      const originalToken = localStorage.getItem('original_admin_token');
      if (originalToken) {
        localStorage.setItem('session_token', originalToken);
        localStorage.removeItem('original_admin_token');
        window.location.href = '/dashboard?t=' + Date.now();
      }
    }
  };

  const value = {
    user,
    setUser,
    loading,
    checkAuth,
    logout,
    getAuthHeaders,
    impersonate,
    stopImpersonation
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
